import React from 'react';
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  LinearProgress,
  Grid,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlaceIcon from '@mui/icons-material/Place';
import FlightIcon from '@mui/icons-material/Flight';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DirectionsRailwayIcon from '@mui/icons-material/DirectionsRailway';
import { TripData } from './TripSelectionStep';
import { formatDate } from '../../../../../utils/dateUtils';

// Interfaces
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
}

interface EmailTemplate {
  subject: string;
  body: string;
  includeCalendarInvite: boolean;
  includeTripDetails: boolean;
}

interface TripReviewStepProps {
  contacts: Contact[];
  trip: TripData | null;
  emailTemplate: EmailTemplate;
  onSend: () => void;
  sending: boolean;
  sendingProgress: number;
}

const TripReviewStep: React.FC<TripReviewStepProps> = ({
  contacts,
  trip,
  emailTemplate,
  onSend,
  sending,
  sendingProgress,
}) => {
  if (!trip) {
    return (
      <Typography>No trip selected. Please go back and select a trip.</Typography>
    );
  }

  // Get contact display name
  const getContactName = (contact: Contact) => {
    return `${contact.firstName} ${contact.lastName}`.trim() || contact.email;
  };

  // Format email preview (replace placeholders)
  const getEmailPreview = (contact: Contact) => {
    const contactName = getContactName(contact);
    const departureDate = trip ? formatDate(trip.departureDate) : '';
    
    return {
      subject: emailTemplate.subject
        .replace('{{contactName}}', contactName)
        .replace('{{departureDate}}', departureDate)
        .replace('{{organizationName}}', 'Your Organization'),
      body: emailTemplate.body
        .replace(/{{contactName}}/g, contactName)
        .replace(/{{departureDate}}/g, departureDate)
        .replace(/{{organizationName}}/g, 'Your Organization')
    };
  };

  // Get transportation icon
  const getTransportIcon = (type?: string) => {
    if (!type) return <FlightIcon />;
    
    const lowercaseType = type.toLowerCase();
    if (lowercaseType.includes('flight') || lowercaseType.includes('plane') || lowercaseType.includes('air')) {
      return <FlightIcon />;
    } else if (lowercaseType.includes('car') || lowercaseType.includes('drive')) {
      return <DirectionsCarIcon />;
    } else if (lowercaseType.includes('train') || lowercaseType.includes('rail')) {
      return <DirectionsRailwayIcon />;
    } else if (lowercaseType.includes('boat') || lowercaseType.includes('cruise') || lowercaseType.includes('ship')) {
      return <DirectionsBoatIcon />;
    }
    
    return <FlightIcon />;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review and Send Trip Invitations
      </Typography>
      
      <Grid container spacing={3}>
        {/* Trip details card */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" component="div" gutterBottom>
                Trip Details
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" component="div">
                  {trip.tripName}
                </Typography>
                
                <Chip 
                  label={trip.status || 'Planned'}
                  color="primary"
                  size="small"
                  sx={{ ml: 1, textTransform: 'capitalize' }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PlaceIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  Destination: {trip.destination || 'To be announced'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  Departure: {formatDate(trip.departureDate)}
                  {trip.returnDate && ` - Return: ${formatDate(trip.returnDate)}`}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FlightIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">
                  Transportation: {trip.transportationType || 'To be determined'}
                </Typography>
              </Box>
              
              {trip.cost && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    Cost: {trip.cost} {trip.currency || 'USD'}
                  </Typography>
                </Box>
              )}
              
              {trip.accommodationDetails && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Accommodation:</strong> {trip.accommodationDetails}
                  </Typography>
                </Box>
              )}
              
              {trip.tripDescription && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Description:</strong> {trip.tripDescription}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Email preview */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" component="div" gutterBottom>
                Email Preview
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="body2">
                  {getEmailPreview(contacts[0]).subject}
                </Typography>
              </Box>
              
              <Typography variant="subtitle2" color="text.secondary">
                Body
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mt: 1, 
                  maxHeight: 200, 
                  overflow: 'auto',
                  bgcolor: 'background.default' 
                }}
              >
                <Typography 
                  variant="body2" 
                  component="div"
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace'
                  }}
                >
                  {getEmailPreview(contacts[0]).body}
                </Typography>
              </Paper>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Options:
                </Typography>
                <Typography variant="body2">
                  {emailTemplate.includeCalendarInvite ? '✓' : '✗'} Include calendar invite
                </Typography>
                <Typography variant="body2">
                  {emailTemplate.includeTripDetails ? '✓' : '✗'} Include trip details attachment
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Recipients */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Recipients ({contacts.length})
          </Typography>
          
          <Paper variant="outlined">
            <List sx={{ maxHeight: 200, overflow: 'auto' }}>
              {contacts.map((contact, index) => (
                <React.Fragment key={contact.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getContactName(contact)}
                      secondary={contact.email}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Send button and progress */}
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {sending ? (
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <LinearProgress 
              variant="determinate" 
              value={sendingProgress} 
              sx={{ mb: 2, height: 10, borderRadius: 5 }}
            />
            <Typography align="center" variant="body2" color="text.secondary">
              Sending invitations... {sendingProgress}%
            </Typography>
          </Box>
        ) : (
          <Button
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            onClick={onSend}
            size="large"
          >
            Send Trip Invitations to {contacts.length} Contacts
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default TripReviewStep;
